/**
 * /devhub/secrets — Secrets & API keys (operator). Surface Layer (/01).
 * REQ-001.91 DevHub UI rebuild · Phase 4 · Area E3.
 *
 * The operator board for secret slots and secret requests. Two LIVE reads over the governed
 * public BFF (anon key + user JWT + RLS — never service-role, never raw SQL from the browser):
 *
 *   GET /api/secret-slots/status   → the slot status board (slot / provider / storage target / status)
 *   GET /api/secret-requests       → the request ceremony rows (request_id / slot / purpose / status)
 *
 * Both endpoints honestly return `blocked_remote_persistence_unavailable` until the secret_requests
 * runtime migration is applied; this surface renders that as an actionable blocked state rather than
 * faking data. The bundled request inbox (`SecretRequestInboxPanel`) is reused unchanged for the
 * request ceremony.
 *
 * BINDING INVARIANT — value entry is NEVER in the browser. The board shows status + the request
 * ceremony only. A secret VALUE is provided out-of-band on the operator's Mac (local Keychain),
 * and submitting on a fragile session requires step-up. There is deliberately no value-entry field
 * here: no `value` / `secret` / `apiKey` field exists on the contract, nothing is sent to the cloud,
 * and nothing is echoed, logged, or rendered. paid_model_called=false — this surface calls no model.
 *
 * Operator-only (routePolicy plane guard + DevHubPanelPage self-guard). Read-only: no run, mutation,
 * tool call, or SQL is issued from here. Zero inline styles — Carbon tokens via the `.view-*` classes.
 * Render-loop-safe: live reads run in a single effect with an AbortController; selectors return
 * stored references.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Column,
  Grid,
  InlineNotification,
  Tag,
  Tile,
} from '@carbon/react';
import {
  CheckmarkFilled,
  Locked,
  Time,
  WarningAltFilled,
} from '@carbon/icons-react';
import { createFileRoute } from '@tanstack/react-router';

import { bffGet } from '@lib/idl-bridge/bff-read-client';
import {
  SECRET_REQUEST_STATUS_LABELS,
  type SecretRequestStatus,
} from '@contracts/secret-request.contract';
import { SecretRequestInboxPanel } from '../features/secrets/SecretRequestInboxPanel';
import { DevHubPanelPage } from '../components/devhub/DevHubPanelPage';
import { OperatorDataGrid } from '../components/devhub-kit/OperatorDataGrid';
import { OperatorDetailTearsheet, type DetailField } from '../components/devhub-kit/OperatorDetailTearsheet';
import type { OperatorColumn } from '@contracts/devhub-kit.contract';

export const Route = createFileRoute('/devhub/secrets')({ component: SecretsPage });

/** Honest board buckets derived from the request lifecycle (requested / pending / fulfilled). */
type BoardBucket = 'requested' | 'pending' | 'fulfilled';

const BUCKET_FOR_STATUS: Partial<Record<SecretRequestStatus, BoardBucket>> = {
  requested: 'requested',
  shown_to_operator: 'requested',
  submitted: 'pending',
  validating: 'pending',
  invalid_reenter: 'pending',
  stored: 'fulfilled',
  available: 'fulfilled',
};

const BUCKET_LABEL: Record<BoardBucket, string> = {
  requested: 'Requested',
  pending: 'Pending',
  fulfilled: 'Fulfilled',
};

/** Status-indicator pattern: a bucket is icon + text (never colour-only) — WCAG 2.2 AA. */
const BUCKET_ICON: Record<BoardBucket, typeof Time> = {
  requested: Time,
  pending: WarningAltFilled,
  fulfilled: CheckmarkFilled,
};

const BUCKET_TAG: Record<BoardBucket, 'cool-gray' | 'teal' | 'green'> = {
  requested: 'cool-gray',
  pending: 'teal',
  fulfilled: 'green',
};

const APPLY_HINT =
  'Apply the secret_requests runtime migration (secret_slots + secret_requests) to enable the live status board. Until then the request ceremony still records governed candidates.';

/** A live secret-slot row (governance metadata only — there is NO value column anywhere). */
interface SecretSlotRow {
  readonly slot: string;
  readonly provider: string;
  readonly storageTarget: string;
  readonly status?: string;
}

/** A live secret-request row, reduced to board fields (no value, ever). */
interface SecretRequestBoardRow {
  readonly requestId: string;
  readonly secretSlot: string;
  readonly provider: string;
  readonly status: SecretRequestStatus;
  readonly redactedFingerprint?: string;
}

type ReadState = 'loading' | 'live' | 'blocked' | 'unauthorized' | 'error';

interface BoardData {
  readonly slotsState: ReadState;
  readonly slots: readonly SecretSlotRow[];
  readonly requestsState: ReadState;
  readonly requests: readonly SecretRequestBoardRow[];
}

const INITIAL_BOARD: BoardData = {
  slotsState: 'loading',
  slots: [],
  requestsState: 'loading',
  requests: [],
};

interface SlotsEnvelope {
  ok?: boolean;
  status?: string;
  slots?: readonly { slot?: unknown; provider?: unknown; storage_target?: unknown; status?: unknown }[];
}

interface RequestsEnvelope {
  ok?: boolean;
  status?: string;
  requests?: readonly {
    request_id?: unknown;
    secret_slot?: unknown;
    provider?: unknown;
    status?: unknown;
    redacted_fingerprint?: unknown;
  }[];
}

const KNOWN_REQUEST_STATUSES = new Set<string>(Object.keys(SECRET_REQUEST_STATUS_LABELS));

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function mapSlots(envelope: SlotsEnvelope | undefined): SecretSlotRow[] {
  const rows = Array.isArray(envelope?.slots) ? envelope!.slots! : [];
  const out: SecretSlotRow[] = [];
  for (const r of rows) {
    const slot = asString(r?.slot);
    if (slot.length === 0) continue;
    out.push({
      slot,
      provider: asString(r?.provider),
      storageTarget: asString(r?.storage_target) || 'keychain_or_envelope',
      status: typeof r?.status === 'string' ? r.status : undefined,
    });
  }
  return out;
}

function mapRequests(envelope: RequestsEnvelope | undefined): SecretRequestBoardRow[] {
  const rows = Array.isArray(envelope?.requests) ? envelope!.requests! : [];
  const out: SecretRequestBoardRow[] = [];
  for (const r of rows) {
    const requestId = asString(r?.request_id);
    const status = asString(r?.status);
    if (requestId.length === 0 || !KNOWN_REQUEST_STATUSES.has(status)) continue;
    const fingerprint = asString(r?.redacted_fingerprint);
    out.push({
      requestId,
      secretSlot: asString(r?.secret_slot),
      provider: asString(r?.provider),
      status: status as SecretRequestStatus,
      redactedFingerprint: fingerprint.length > 0 ? fingerprint : undefined,
    });
  }
  return out;
}

/** Map a BridgeResponse outcome to an honest read state (live data, blocked, auth, or transport). */
function readStateFor(
  ok: boolean,
  envelopeStatus: string | undefined,
  errorCode: string | undefined,
): ReadState {
  if (errorCode === 'unauthorized') return 'unauthorized';
  if (!ok) {
    if (envelopeStatus === 'blocked_remote_persistence_unavailable') return 'blocked';
    return 'error';
  }
  if (envelopeStatus === 'blocked_remote_persistence_unavailable') return 'blocked';
  return 'live';
}

function SecretsPage() {
  return (
    <DevHubPanelPage
      title="Secrets & API keys"
      subtitle="Slot status board and the request ceremony. Secret values are provided locally in the Keychain — never entered, sent, or shown in the browser."
      status={{ label: 'value entry is local-only', live: false }}
    >
      <SecretsBoard />
    </DevHubPanelPage>
  );
}

function SecretsBoard() {
  const [board, setBoard] = useState<BoardData>(INITIAL_BOARD);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    async function load() {
      const [slotsRes, requestsRes] = await Promise.all([
        bffGet<SlotsEnvelope>('/api/secret-slots/status', undefined, { signal: controller.signal }),
        bffGet<RequestsEnvelope>('/api/secret-requests', undefined, { signal: controller.signal }),
      ]);
      if (cancelled) return;

      const slotsEnvelope = slotsRes.ok ? slotsRes.data : undefined;
      const requestsEnvelope = requestsRes.ok ? requestsRes.data : undefined;

      setBoard({
        slotsState: readStateFor(
          slotsRes.ok,
          slotsEnvelope?.status,
          slotsRes.ok ? undefined : slotsRes.error.code,
        ),
        slots: mapSlots(slotsEnvelope),
        requestsState: readStateFor(
          requestsRes.ok,
          requestsEnvelope?.status,
          requestsRes.ok ? undefined : requestsRes.error.code,
        ),
        requests: mapRequests(requestsEnvelope),
      });
    }

    void load();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const counts = useMemo(() => {
    const acc: Record<BoardBucket, number> = { requested: 0, pending: 0, fulfilled: 0 };
    for (const r of board.requests) {
      const bucket = BUCKET_FOR_STATUS[r.status];
      if (bucket) acc[bucket] += 1;
    }
    return acc;
  }, [board.requests]);

  const buckets = useMemo<BoardBucket[]>(() => ['requested', 'pending', 'fulfilled'], []);

  return (
    <div className="surface-grid">
      <Grid condensed>
        {/* Binding invariant — the security spine of this surface. Always shown. */}
        <Column sm={4} md={8} lg={16}>
          <InlineNotification
            kind="info"
            lowContrast
            hideCloseButton
            title="Value entry happens in the local Keychain — step-up required"
            subtitle="This board shows status and the request ceremony only. A secret value is never entered, sent, echoed, or rendered in the browser: it is provided out-of-band on your Mac (macOS Keychain via the Secret Inbox), and submitting on a fragile session requires step-up. No value column exists on the contract, and the cloud never sees plaintext."
          />
        </Column>

        {/* Status-indicator pattern — bucket counts as icon + text (never colour-only). */}
        <Column sm={4} md={8} lg={16}>
          <div className="view-tile__row">
            <h2 className="view-tile__value">Slot status board</h2>
            <div className="notification-row__meta">
              {buckets.map((bucket) => {
                const Icon = BUCKET_ICON[bucket];
                return (
                  <Tag key={bucket} type={BUCKET_TAG[bucket]} size="sm">
                    <Icon aria-hidden="true" /> {BUCKET_LABEL[bucket]}: {counts[bucket]}
                  </Tag>
                );
              })}
            </div>
          </div>
        </Column>

        {/* The live slot status board. */}
        <Column sm={4} md={8} lg={16}>
          <SlotStatusBoard state={board.slotsState} slots={board.slots} requests={board.requests} />
        </Column>

        {/* The request ceremony — reused inbox panel (live secret_requests rows, no value field). */}
        <Column sm={4} md={8} lg={16}>
          <SecretRequestInboxPanel title="Request ceremony" />
        </Column>

        <Column sm={4} md={8} lg={16}>
          <p className="view-tile__label">
            Read-only. A secret is never executed, mutated, or revealed from this surface. The slot
            board and request ceremony are governed reads (anon key + your operator session + RLS);
            value handling stays on your Mac. Source of truth: public.secret_slots /
            public.secret_requests (no value column exists in either).
          </p>
        </Column>
      </Grid>
    </div>
  );
}

interface SlotStatusBoardProps {
  readonly state: ReadState;
  readonly slots: readonly SecretSlotRow[];
  readonly requests: readonly SecretRequestBoardRow[];
}

/** A slot row joined with its latest request status — the OperatorDataGrid row (NO value, ever). */
interface SlotGridRow extends SecretSlotRow {
  readonly requestStatus?: SecretRequestStatus;
}

function SlotStatusBoard({ state, slots, requests }: SlotStatusBoardProps) {
  const [selected, setSelected] = useState<SlotGridRow | null>(null);

  /** Latest request per slot, to show the ceremony status alongside each slot. */
  const latestRequestBySlot = useMemo(() => {
    const map = new Map<string, SecretRequestBoardRow>();
    for (const r of requests) {
      if (r.secretSlot.length > 0 && !map.has(r.secretSlot)) map.set(r.secretSlot, r);
    }
    return map;
  }, [requests]);

  const gridRows = useMemo<SlotGridRow[]>(
    () => slots.map((s) => ({ ...s, requestStatus: latestRequestBySlot.get(s.slot)?.status })),
    [slots, latestRequestBySlot]
  );

  const columns = useMemo<OperatorColumn<SlotGridRow>[]>(
    () => [
      { key: 'slot', header: 'Slot', render: (r) => <code className="view-tile__label">{r.slot}</code> },
      { key: 'provider', header: 'Provider', filterable: true, render: (r) => <>{r.provider || '—'}</> },
      {
        key: 'storageTarget',
        header: 'Storage target',
        render: (r) => (
          <Tag type="cool-gray" size="sm">
            <Locked aria-hidden="true" /> {r.storageTarget}
          </Tag>
        ),
      },
      {
        key: 'requestStatus',
        header: 'Request status',
        isStatus: true,
        render: (r) => <SlotRequestStatusCell status={r.requestStatus} />,
      },
    ],
    []
  );

  // Special honest states the generic grid error cannot express — kept verbatim.
  if (state === 'unauthorized') {
    return (
      <InlineNotification
        kind="info"
        lowContrast
        hideCloseButton
        title="Sign in to load the slot status board"
        subtitle="The slot board is a governed read scoped to your operator session. Sign in with an operator role to load live secret-slot status."
      />
    );
  }

  if (state === 'blocked') {
    return (
      <Tile className="view-tile">
        <div className="dtx-grid__empty" role="status">
          <p className="view-tile__label">
            <Locked aria-hidden="true" /> Slot status board not yet available
          </p>
          <p className="view-tile__value">{APPLY_HINT}</p>
        </div>
      </Tile>
    );
  }

  if (state === 'error') {
    return (
      <InlineNotification
        kind="warning"
        lowContrast
        hideCloseButton
        title="Could not load the slot status board"
        subtitle="The governed read did not return. The request ceremony below still works; retry the board by reloading this view."
      />
    );
  }

  // Loading + live: the real @carbon/ibm-products Datagrid (loading skeleton + empty handled by the kit).
  // NO AILabel here by design — a secret slot is NEVER touched by a model (values stay local), so an AI
  // affordance would be a false claim. The row opens a VIEW-ONLY detail (no edit, no Send-to-AI).
  return (
    <Tile className="view-tile">
      <OperatorDataGrid<SlotGridRow>
        ariaLabel={`Secret slot status board · ${slots.length} slot${slots.length === 1 ? '' : 's'} · governance metadata only (no value column)`}
        columns={columns}
        rows={gridRows}
        getRowId={(r) => r.slot}
        onRowClick={setSelected}
        loading={state === 'loading'}
        emptyLabel="No secret slots registered yet — slots appear once governance metadata is published to public.secret_slots (provider + storage target only, never a value)."
      />
      <OperatorDetailTearsheet
        open={selected != null}
        onClose={() => setSelected(null)}
        title={selected ? selected.slot : ''}
        subtitle="Governance metadata only — a secret value is never stored, sent, or shown here."
        mode="view"
        onModeChange={() => undefined}
        fields={selected ? slotDetailFields(selected) : []}
      />
    </Tile>
  );
}

/** View-only detail fields for a secret slot — explicitly states the no-value security invariant. */
function slotDetailFields(r: SlotGridRow): DetailField[] {
  return [
    { label: 'Slot', value: <code>{r.slot}</code> },
    { label: 'Provider', value: r.provider || '—' },
    {
      label: 'Storage target',
      value: (
        <Tag type="cool-gray" size="sm">
          <Locked aria-hidden="true" /> {r.storageTarget}
        </Tag>
      ),
    },
    { label: 'Request status', value: <SlotRequestStatusCell status={r.requestStatus} /> },
    { label: 'Value', value: 'Never stored, sent, or shown here — provided out-of-band in the macOS Keychain (step-up on a fragile session).' },
  ];
}

/** Per-slot request status, rendered with the icon + text status-indicator pattern. */
function SlotRequestStatusCell({ status }: { readonly status?: SecretRequestStatus }) {
  if (status === undefined) {
    return (
      <Tag type="gray" size="sm">
        No request
      </Tag>
    );
  }
  const bucket = BUCKET_FOR_STATUS[status];
  if (bucket === undefined) {
    return (
      <Tag type="gray" size="sm">
        {SECRET_REQUEST_STATUS_LABELS[status]}
      </Tag>
    );
  }
  const Icon = BUCKET_ICON[bucket];
  return (
    <Tag type={BUCKET_TAG[bucket]} size="sm">
      <Icon aria-hidden="true" /> {SECRET_REQUEST_STATUS_LABELS[status]}
    </Tag>
  );
}
